import React from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import L from 'leaflet';
import 'leaflet-routing-machine';
import { Map, TileLayer, MapLayer, LayerGroup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import ClinicianMarker from './ClinicianMarker';
import AssignmentMarker from './AssignmentMarker';
import { refSetter } from '../../../utils/helpers';
import locationsForPosting from '../../../propTypes/entities/locationsForPosting';
import { appSettingsPropTypes, appSettingsDefault } from '../../../propTypes/entities/appSettings';
import globals from '../../../globals/communityGlobals';

class RoutingMachine extends MapLayer {
  getWaypoints = locations =>
    locations.filter(l => !!l).map(location => L.latLng(location.latitude, location.longitude));

  createLeafletElement() {
    const { locations } = this.props;
    const waypoints = this.getWaypoints(locations);

    return L.Routing.control({
      router: L.Routing.osrmv1({
        serviceUrl: `${globals.urls.apiUrl}/OSRMProxy/Route`,
      }),
      waypoints,
      createMarker: () => false,
      addWaypoints: false,
      draggableWaypoints: false,
    })
      .addTo(this.context.map)
      .getPlan();
  }

  updateLeafletElement(fromProps, toProps) {
    if (
      fromProps.locations.length !== toProps.locations.length ||
      fromProps.locations
        .filter(e => !!e)
        .map(r => r.name)
        .join() !==
        toProps.locations
          .filter(e => !!e)
          .map(r => r.name)
          .join()
    ) {
      const waypoints = this.getWaypoints(toProps.locations);

      this.leafletElement.setWaypoints(waypoints);
    }
  }
}

class ClinicianLocator extends React.Component {
  componentWillReceiveProps = nextProps => {
    const { selectedClinicianId, postings } = nextProps;
    const newSelectedPosting = postings.find(el => el.clinicianId === selectedClinicianId);

    if (nextProps.selectedClinicianId === this.props.selectedClinicianId) {
      return;
    }

    if (newSelectedPosting) {
      if (newSelectedPosting.clinicianLocation) {
        this.mapView.leafletElement.flyTo(
          [newSelectedPosting.clinicianLocation.latitude, newSelectedPosting.clinicianLocation.longitude],
          this.defaultZoom,
        );
      }
    } else {
      if (postings.length === 0) return;
      const bounds = L.latLngBounds([]);
      postings.forEach(posting => {
        if (posting.clinicianLocation) {
          bounds.extend([posting.clinicianLocation.latitude, posting.clinicianLocation.longitude]);
        }
      });
      this.mapView.leafletElement.fitBounds(bounds);
    }
  };

  shouldComponentUpdate = (nextProps, nextState) => !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

  setRef = refSetter.bind(this);

  getMap = () => this.mapView.leafletElement;

  getDayPlanLayer = () => {
    let assignments = null;
    let locations = [];
    const { selectedClinicianId, postings } = this.props;
    const selectedPosting = postings.find(el => el.clinicianId === selectedClinicianId);

    if (!!selectedPosting && selectedPosting.assignments.length > 0) {
      assignments = selectedPosting.assignments.map((assignment, index) => (
        <AssignmentMarker
          key={`assignmentMarker${assignment.id}`}
          orderMark={String.fromCharCode(index + 65)}
          assignment={assignment}
        />
      ));
      locations = [
        selectedPosting.clinicianLocation,
        ...selectedPosting.assignments.filter(a => !!a).map(assignment => assignment.location),
      ];
    }

    return (
      <LayerGroup key={'dayPlanLayer'}>
        <RoutingMachine locations={locations} map={this.getMap} />
        <MarkerClusterGroup>{assignments}</MarkerClusterGroup>
      </LayerGroup>
    );
  };

  defaultZoom = 10;
  defaultCenter = [51.505, -0.09];

  render() {
    const { postings, appSettings } = this.props;
    const markers = postings
      .filter(r => !!r.clinicianLocation)
      .map(({ ...props }) => (
        <ClinicianMarker
          clinicianColor={props.clinicianColor}
          appSettings={appSettings}
          posting={props}
          key={`clinicianMarker${props.clinicianId}`}
          location={props.clinicianLocation}
        />
      ));

    return (
      <Map
        className="markercluster-map"
        useFlyTo
        center={this.defaultCenter}
        zoom={this.defaultZoom}
        style={{ width: '100%', minHeight: '400px' }}
        ref={this.setRef('mapView')}
      >
        <TileLayer attribution="" url={`${globals.urls.apiUrl}/OpenStreetMapProxy/GetTile/{z}/{x}/{y}`} />
        {this.getDayPlanLayer()}
        <MarkerClusterGroup>{markers}</MarkerClusterGroup>
      </Map>
    );
  }
}

ClinicianLocator.defaultProps = {
  appSettings: appSettingsDefault,
  selectedClinicianId: null,
  selectedPosting: null,
};

ClinicianLocator.propTypes = {
  appSettings: appSettingsPropTypes,
  postings: PropTypes.arrayOf(locationsForPosting).isRequired,
  selectedClinicianId: PropTypes.number,
};

export default ClinicianLocator;
